import React from "react";
import { Link, useLocation } from "react-router-dom";
import { ChefHat, BookmarkSimple, Receipt, Sparkle, Package, SignOut, Lock, Flask, FileText, MagnifyingGlass, ChatCircle, BookOpen, Notepad, ListChecks, ChartBar, ShoppingCart } from "@phosphor-icons/react";

import { useAuth } from "../lib/auth";

export default function Header() {
  const { pathname } = useLocation();
  const { isAdmin, username, openLogin, logout } = useAuth();
  const linkCls = (active) =>
    `px-3 py-1.5 rounded-md text-sm tracking-wide transition-colors duration-200 ${
      active
        ? "bg-[#0E5232] text-[#FDF9EE]"
        : "text-white/80 hover:text-white hover:bg-[#0E5232]/60"
    }`;

  return (
    <header
      className="sticky top-0 z-40 w-full bg-[#0A3B24] text-white border-b border-[#0E5232]"
      data-testid="kostra-header"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-3 flex items-center gap-3 md:gap-5 flex-wrap">
        <Link to="/" className="flex items-center gap-3 group shrink-0" data-testid="kostra-logo-link">
          <div className="h-10 w-10 rounded-md bg-[#D4AF37]/15 border border-[#D4AF37]/40 flex items-center justify-center">
            <ChefHat size={22} weight="duotone" color="#D4AF37" />
          </div>
          <div className="leading-tight">
            <div className="font-display text-xl tracking-tight">
              KOSTRA
              <span className="ml-2 align-middle text-[10px] font-medium tracking-[0.2em] uppercase text-[#D4AF37] hidden xl:inline">
                Pro
              </span>
            </div>
            <div className="text-[11px] text-white/70 tracking-wide hidden sm:block">
              The Food Costing Expert <span className="text-[#D4AF37]">·</span> by Anupam
            </div>
          </div>
        </Link>

        <nav className="flex items-center gap-1 flex-wrap order-3 md:order-2 w-full md:w-auto md:ml-2 overflow-x-auto" data-testid="kostra-nav">
          <Link to="/" className={linkCls(pathname === "/")} data-testid="nav-calculator">
            <span className="inline-flex items-center gap-2 whitespace-nowrap"><Receipt size={16} weight="duotone" />Calculator</span>
          </Link>
          <Link to="/discover" className={linkCls(pathname === "/discover")} data-testid="nav-discover">
            <span className="inline-flex items-center gap-2 whitespace-nowrap"><MagnifyingGlass size={16} weight="duotone" />Discover</span>
          </Link>
          <Link to="/inspiration" className={linkCls(pathname === "/inspiration")} data-testid="nav-inspiration">
            <span className="inline-flex items-center gap-2 whitespace-nowrap"><Sparkle size={16} weight="duotone" />Recipe Library</span>
          </Link>
          <Link to="/production" className={linkCls(pathname === "/production")} data-testid="nav-production">
            <span className="inline-flex items-center gap-2 whitespace-nowrap"><Package size={16} weight="duotone" />Production Plan</span>
          </Link>
          <Link to="/purchases" className={linkCls(pathname === "/purchases")} data-testid="nav-purchases">
            <span className="inline-flex items-center gap-2 whitespace-nowrap"><ShoppingCart size={16} weight="duotone" />Quick Purchases</span>
          </Link>
          <Link to="/inventory-check" className={linkCls(pathname === "/inventory-check")} data-testid="nav-inventory-check">
            <span className="inline-flex items-center gap-2 whitespace-nowrap"><Package size={16} weight="duotone" />Inventory Check</span>
          </Link>
          <Link to="/menu" className={linkCls(pathname === "/menu")} data-testid="nav-menu">
            <span className="inline-flex items-center gap-2 whitespace-nowrap"><Notepad size={16} weight="duotone" />Menu Creator</span>
          </Link>
          <Link to="/ingredients" className={linkCls(pathname === "/ingredients")} data-testid="nav-ingredients">
            <span className="inline-flex items-center gap-2 whitespace-nowrap"><ListChecks size={16} weight="duotone" />Ingredients Master</span>
          </Link>
          <Link to="/engineering" className={linkCls(pathname === "/engineering")} data-testid="nav-engineering">
            <span className="inline-flex items-center gap-2 whitespace-nowrap"><ChartBar size={16} weight="duotone" />Menu Engineering</span>
          </Link>
          <Link to="/invoice" className={linkCls(pathname === "/invoice")} data-testid="nav-invoice">
            <span className="inline-flex items-center gap-2 whitespace-nowrap"><Receipt size={16} weight="duotone" />R&amp;D Invoice</span>
          </Link>
          <Link to="/rnd" className={linkCls(pathname === "/rnd")} data-testid="nav-rnd">
            <span className="inline-flex items-center gap-2 whitespace-nowrap"><Flask size={16} weight="duotone" />R&amp;D Lab</span>
          </Link>
          <Link to="/samples" className={linkCls(pathname === "/samples")} data-testid="nav-samples">
            <span className="inline-flex items-center gap-2 whitespace-nowrap"><FileText size={16} weight="duotone" />Sample R&amp;D Reports</span>
          </Link>
          <Link to="/askme" className={linkCls(pathname === "/askme")} data-testid="nav-askme">
            <span className="inline-flex items-center gap-2 whitespace-nowrap"><ChatCircle size={16} weight="duotone" />Ask Me</span>
          </Link>
          <Link to="/guide" className={linkCls(pathname === "/guide")} data-testid="nav-guide">
            <span className="inline-flex items-center gap-2 whitespace-nowrap"><BookOpen size={16} weight="duotone" />Guide</span>
          </Link>
          <Link to="/library" className={linkCls(pathname === "/library")} data-testid="nav-library">
            <span className="inline-flex items-center gap-2 whitespace-nowrap"><BookmarkSimple size={16} weight="duotone" />My Recipes</span>
          </Link>
        </nav>

        <div className="ml-auto flex items-center gap-2 order-2 md:order-3" data-testid="auth-bar">
          {isAdmin ? (
            <>
              <span
                className="hidden sm:inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#0A3B24] bg-[#D4AF37] border border-[#D4AF37] px-2.5 py-1 rounded-full"
                data-testid="auth-username-pill"
              >
                <span className="h-1.5 w-1.5 rounded-full bg-[#0A3B24]" />
                {username || "Admin"}
              </span>
              <button
                type="button"
                onClick={logout}
                className="inline-flex items-center gap-1.5 text-xs font-semibold tracking-wide text-white/85 bg-white/10 hover:bg-white/20 border border-white/20 hover:border-white/40 px-3 py-1.5 rounded-md transition"
                data-testid="auth-logout-btn"
              >
                <SignOut size={14} weight="bold" /> Logout
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={() => openLogin()}
              className="inline-flex items-center gap-1.5 text-xs font-semibold tracking-wide text-[#0A3B24] bg-[#D4AF37] hover:bg-[#C5A028] border border-[#D4AF37] px-3 py-1.5 rounded-md transition"
              data-testid="auth-login-btn"
            >
              <Lock size={14} weight="bold" /> Admin Login
            </button>
          )}
        </div>
      </div>
    </header>
  );
}
